/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useLogoSource } from '@/shared/hooks';
import { cn } from '@/shared/ui/utils/tailwind';

type HousienBrandLogoVariant = 'full' | 'icon';

interface HousienBrandLogoProps {
  variant?: HousienBrandLogoVariant;
  height?: number;
  className?: string;
  alt?: string;
  /**
   * Set when the logo sits on a dark background. Swaps the icon for the
   * light-ink asset, and — on the `full` lockup — hands the wordmark over to
   * `currentColor` so the caller's ink applies: the default wine wordmark is
   * all but invisible on a dark ground, and a hardcoded light value here
   * would only be right for one of them.
   */
  onDark?: boolean;
}

const ICON_DEFAULTS = {
  height: 32,
} as const;

const FULL_DEFAULTS = {
  height: 64,
  iconWidth: 64,
  iconMarginLeft: -16,
  iconMarginRight: -12,
  iconMarginTop: 5,
  textFontSize: '1.5rem',
  textPaddingBottom: 14,
  textLetterSpacing: '-0.01em',
} as const;

/**
 * Self-contained Housien brand logo component.
 *
 * - `variant="full"`: house+H icon image followed by "ousien" wordmark text (default)
 * - `variant="icon"`: house+H icon image only
 *
 * Uses `useLogoSource()` internally for the brand logo path.
 */
const HousienBrandLogo: React.FC<HousienBrandLogoProps> = ({
  variant = 'full',
  height,
  className,
  alt = 'Housien',
  onDark = false,
}) => {
  const logoSource = useLogoSource(onDark);
  const textColor = onDark ? 'currentColor' : 'var(--button-primary-hover-bg)';

  if (variant === 'icon') {
    const h = height ?? ICON_DEFAULTS.height;
    return (
      <img
        src={logoSource}
        alt={alt}
        className={cn('select-none block object-contain', className)}
        style={{
          height: `${h}px`,
          width: 'auto',
        }}
        draggable={false}
        translate="no"
      />
    );
  }

  const h = height ?? FULL_DEFAULTS.height;
  const scale = h / FULL_DEFAULTS.height;

  return (
    <span className={cn('inline-flex items-end gap-0 shrink-0', className)} translate="no">
      <img
        src={logoSource}
        alt={alt}
        className="select-none block"
        style={{
          height: `${h}px`,
          width: `${FULL_DEFAULTS.iconWidth * scale}px`,
          objectFit: 'contain',
          marginLeft: `${FULL_DEFAULTS.iconMarginLeft * scale}px`,
          marginRight: `${FULL_DEFAULTS.iconMarginRight * scale}px`,
          marginTop: `${FULL_DEFAULTS.iconMarginTop * scale}px`,
        }}
        draggable={false}
      />
      <span
        className="whitespace-nowrap select-none leading-none"
        style={{
          fontFamily: 'var(--font-family, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif)',
          fontSize: `${parseFloat(FULL_DEFAULTS.textFontSize) * scale}rem`,
          fontWeight: 350,
          color: textColor,
          letterSpacing: FULL_DEFAULTS.textLetterSpacing,
          paddingBottom: `${FULL_DEFAULTS.textPaddingBottom * scale}px`,
        }}
      >
        ousien
      </span>
    </span>
  );
};

export default HousienBrandLogo;
